import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/button";
import FitnessToolsResultCard from "../../../components/cards/FitnessToolsResultCard";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import Input from "../../../components/input";
import Loader from "../../../components/loader";
import OtherCalculatorSection from "../../../components/OtherCalculatorSection";
import AISkeleton from "../../../components/skeleton/AISkeleton";
import { useTranslate } from "../../../context/TranslationContext";
import useChartPeriod from "../../../hooks/useChartPeriod";
import {
  useCreateHydrationMutation,
  useGetHydrationQuery,
  useUpdateHydrationMutation,
} from "../../../redux/Apis/Calculator/Hydration";
import { useCreateAISuggestionMutation } from "../../../redux/Apis/TrackLecture";
import { useStaticData } from "../../../utils/data";
import { formatAISuggestion } from "../../../utils/helper";

const Hydration = () => {
  const { t } = useTranslate("hydration");
  const lang = useSelector((state) => state.language.lang);
  const { CALCULATORS, CALCULATOR_DATA } = useStaticData();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.authSlice);
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [activityLevel, setActivityLevel] = useState("sedentary");
  const [climate, setClimate] = useState("temperate");
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const { period, dateRange, handlePeriodChange } = useChartPeriod();
  const { startDate, endDate } = dateRange;
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [highlightAI, setHighlightAI] = useState(false);
  const aiRef = useRef(null);
  const [createAISuggestion] = useCreateAISuggestionMutation();
  const [createHydration] = useCreateHydrationMutation();
  const [updateHydration] = useUpdateHydrationMutation();
  const {
    data: hydrationHistoryData,
    refetch: refetchHydration,
    isLoading,
  } = useGetHydrationQuery({ lang }, { skip: !user });
  const {
    data: hydData,
    refetch: refetchHydData,
    isLoading: loadingHyd,
  } = useGetHydrationQuery(
    { startdate: startDate, enddate: endDate, lang },
    { skip: !startDate || !endDate || !user },
  );
  const courseState = location?.state || {};
  // const templateId = courseState?.templateId;
  const courseId = courseState?.course;
  const sectionId = courseState?.sectionId || courseState?.section;
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (aiLoading) {
      const timer = setTimeout(() => setShowSkeleton(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowSkeleton(false);
    }
  }, [aiLoading]);

  useEffect(() => {
    if ((aiLoading || aiSuggestion) && aiRef.current) {
      setTimeout(() => {
        const y =
          aiRef.current.getBoundingClientRect().top + window.pageYOffset - 110; // header height offset

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });

        setHighlightAI(true);
        setTimeout(() => setHighlightAI(false), 3000);
      }, 300);
    }
  }, [aiLoading, aiSuggestion]);

  useEffect(() => {
    if (user) {
      refetchHydration();
    }
  }, [user, refetchHydration]);

  useEffect(() => {
    if (startDate && endDate && user) {
      refetchHydData();
    }
  }, [startDate, endDate, user, refetchHydData]);

  // useEffect(() => {
  //   setMetaTags(
  //     "Traineego | Hydration Calculator",
  //     "Estimate your daily water intake based on weight, activity, and climate to stay hydrated and perform at your best.",
  //   );
  // }, []);

  useEffect(() => {
    if (hydrationHistoryData) {
      setHistoryData(hydrationHistoryData.data || []);
    }
  }, [hydrationHistoryData]);

  const calculateHydration = () => {
    if (!weight || isNaN(weight) || weight <= 0) {
      toast.error(t("message.invalidWeight"));
      return;
    }

    const weightKg =
      weightUnit === "lbs" ? parseFloat(weight) / 2.205 : parseFloat(weight);
    let waterLiters = weightKg * 0.033;

    // 2. Activity Adder (TL-07)
    const activityAdders = {
      sedentary: 0,
      light: 0,
      moderate: 0.35,
      very: 0.7,
      super: 0.7,
    };

    waterLiters += activityAdders[activityLevel] || 0;

    // 3. Climate Adder (TL-07)
    const climateAdders = {
      cold: 0,
      temperate: 0,
      hot: 0.5,
      "very-hot": 0.7,
    };

    waterLiters += climateAdders[climate] || 0;

    // 4. Output
    const liters = waterLiters.toFixed(2);
    const cups = Math.round(waterLiters / 0.25);

    const output = {
      liters,
      cups,
      type: "Hydration",
      result: `${liters} L (about ${cups} cups)`,
    };

    setResult(output);

    if (
      location.state?.sectionIndex !== undefined &&
      location.state?.lectureIndex !== undefined
    ) {
      const storedResults =
        JSON.parse(localStorage.getItem("fitnessResults")) || {};
      storedResults[
        `${location.state.sectionIndex}-${location.state.lectureIndex}`
      ] = output;
      localStorage.setItem("fitnessResults", JSON.stringify(storedResults));
    }
    toast.success(t("message.saveSuccess"));
    return output;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t("result.notYetCalculated");
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatHistoryEntry = (entry) => {
    const createdAt = new Date(entry.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return (
      <div className="d-flex justify-content-between align-items-center">
        <p className="mb-0">{createdAt}</p>
        <p className="mb-0 fw-bold">{entry.result}</p>
      </div>
    );
  };

  const generateAICoach = async (inputResult) => {
    try {
      // if (!courseId || !sectionId) return;

      setAiLoading(true);

      const aiPayload = {
        courseid: courseId,
        sectionid: sectionId,
        learnerid: user._id,
        type: "Hydration",
        result: [
          {
            weight: inputResult.weight,
            activity_level: inputResult.activity_level,
            climate: inputResult.climate,
            hydration_result: inputResult.result,
          },
        ],
      };

      const res = await createAISuggestion({
        payload: aiPayload,
        params: { lang },
      }).unwrap();

      const suggestion = res?.data?.suggestion;
      if (suggestion) setAiSuggestion(suggestion);
    } catch (err) {
      console.log("AI error", err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveResult = async (type, result) => {
    if (!result || !result.liters) {
      toast.error(t("message.saveError"));
      return;
    }

    if (!user) {
      toast.error(t("message.loginFirst"));
      // navigate("/auth/login");
      return;
    }

    try {
      const payload = {
        weight: `${weight} ${weightUnit}`,
        activity_level: activityLevel,
        climate: climate,
        result: `${result.liters} L (${result.cups} cups)`,
      };
      if (courseId) payload.course = courseId;
      const response = await createHydration({
        payload,
        params: { lang },
      }).unwrap();
      toast.success(response.message || t("message.dataSaved"));
      refetchHydration();
      refetchHydData();
      // if (courseId && sectionId && templateId) {
      await generateAICoach(payload);
      // }
    } catch (error) {
      console.error("Error saving Hydration:", error);
      toast.error(t("message.saveFailed"));
    }
  };

  const handleDeleteLast = async () => {
    try {
      const latestEntry = [...historyData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      )[0];
      if (latestEntry) {
        const payload = {
          id: latestEntry._id,
          isactive: false,
          params: { lang },
        };
        await updateHydration(payload).unwrap();
        toast.success(t("message.deleteSuccess"));
        refetchHydration();
        refetchHydData();
      }
    } catch (error) {
      console.error("Error deleting last Hydration:", error);
      toast.error(t("message.deleteError"));
    }
  };

  const toggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  if (isLoading || loadingHyd) {
    return <Loader loading={true} />;
  }

  return (
    <>
      <Helmet>
        <title>
          Hydration Calculator | Daily Water Intake Calculator | Traineego
        </title>

        <meta
          name="description"
          content="Find out how much water you should drink daily based on your body weight, activity level, and lifestyle."
        />

        <meta name="robots" content="index,follow" />
        <link
          rel="canonical"
          href="https://traineego.com/fitness-calculators/hydration"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Hydration Calculator",
            url: "https://traineego.com/fitness-calculators/hydration",
            description:
              "Find out how much water you should drink daily based on your body weight, activity level, and lifestyle.",
            publisher: {
              "@type": "Organization",
              name: "Traineego",
              url: "https://traineego.com",
            },
          })}
        </script>
      </Helmet>
      <Header />
      <section className="banner" style={{ textAlign: "left" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="text-uppercase">{t("title")}</h1>
              <p className="mb-0 p-lg l-grey-text">{t("subtitle")}</p>
            </div>
          </div>

          <div className="row d-flex">
            <div className="col-md-12 mt-4 p-5 fitness-card">
              <div className="row">
                <div className="col-lg-8">
                  <h4 className="text-uppercase mb-4">{t("title")}</h4>
                  <div className="row">
                    <div className="col-md-8">
                      <Input
                        label={t("form.weightLabel")}
                        placeholder={t("form.weightPlaceholder")}
                        required
                        type="number"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <Input
                        label={t("form.unitLabel")}
                        type="select"
                        options={CALCULATOR_DATA.WEIGHT_UNITS}
                        value={weightUnit}
                        onChange={(e) => setWeightUnit(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col-md-6">
                      <Input
                        label={t("form.activityLabel")}
                        type="select"
                        options={CALCULATOR_DATA.ACTIVITY_LEVELS}
                        value={activityLevel}
                        onChange={(e) => setActivityLevel(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t("form.climateLabel")}
                        type="select"
                        options={CALCULATOR_DATA.CLIMATE_OPTIONS}
                        value={climate}
                        onChange={(e) => setClimate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mt-5">
                    <div className="fitness-branded col-12 py-2 px-4 d-flex justify-content-between align-items-center">
                      <p className="grey-text m-0">
                        {t("form.calculatePrompt")}
                      </p>
                      <Button
                        text={t("form.calculateBtn")}
                        onClick={calculateHydration}
                      />
                    </div>
                  </div>
                  {result && location.state?.returnTo && (
                    <div className="row mt-3">
                      <div className="col-12 text-center">
                        <Button
                          text={t("cta.continueLearning")}
                          // onClick={() => navigate(location.state.returnTo)}
                          onClick={() =>
                            navigate(location.state.returnTo, {
                              state: {
                                ...location.state,
                                resultKey: `${location.state.sectionIndex}-${location.state.lectureIndex}-Hydration`,
                                resultData: {
                                  type: "Hydration",
                                  result: result,
                                  value: `Hydration: ${result}`,
                                  timestamp: new Date().toISOString(),
                                },
                              },
                            })
                          }
                          className="site-btn border-btn"
                          style={{ width: "100%", maxWidth: "200px" }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-4 mt-4 mt-lg-0">
                  <div
                    className="h-100 p-3 p-md-4 d-flex flex-column justify-content-center"
                    style={{
                      border: "1px solid white",
                      borderRadius: "1rem",
                      background: "rgba(112,112,112,0.4)",
                    }}
                  >
                    {result ? (
                      <>
                        <h6>
                          {t("result.title")} {result.liters} liters (about{" "}
                          {result.cups} cups).
                        </h6>
                        <p className="p-sm">{t("result.description")}</p>
                      </>
                    ) : (
                      <h6>{t("result.displayText")}</h6>
                    )}
                    <div
                      className="bg-white w-100 p-3 text-center mt-3"
                      style={{
                        border: "0.5px solid gray",
                        height: "auto",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p className="m-0 text-black">{t("cta.title")}</p>
                      <p className="m-0 grey-text p-sm">{t("cta.subtitle")}</p>
                      <Button text={t("cta.button")} to="/courses-tracks" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FitnessToolsResultCard
              type="Hydration"
              result={
                result
                  ? {
                      ...result,
                      timestamp: new Date().toISOString(),
                    }
                  : null
              }
              historyData={historyData}
              showHistory={showHistory}
              toggleHistory={toggleHistory}
              handleSaveResult={handleSaveResult}
              handleDeleteLast={handleDeleteLast}
              formatHistoryEntry={formatHistoryEntry}
              formatDateTime={formatDateTime}
              chartData={hydData?.data || []}
              period={period}
              handlePeriodChange={handlePeriodChange}
            />

            {(aiLoading || aiSuggestion) && (
              <div
                className={`ai-suggestion-box ${highlightAI ? "ai-highlight" : ""}`}
                ref={aiRef}
              >
                <h4 className="text-white mb-3">
                  {/* <FontAwesomeIcon icon={faRobot} color="var(--primaryColor)" />{" "} */}
                  <img
                    src="https://traineego.com/assets/images/favicon-2026.ico"
                    alt="AI Coach"
                    style={{
                      width: "45px",
                      marginRight: "8px",
                      borderRadius: "25px",
                    }}
                  />
                  {t("aiSuggestion.title")}
                </h4>

                {showSkeleton ? (
                  // <p style={{ color: "#aaa" }}>
                  //   <Spinner size="sm" /> {t("aiSuggestion.loading")}
                  // </p>
                  <AISkeleton />
                ) : (
                  <div
                    style={{ color: "#d1d1d6", lineHeight: "1.7" }}
                    dangerouslySetInnerHTML={{
                      __html: formatAISuggestion(aiSuggestion),
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <OtherCalculatorSection
        data={CALCULATORS.filter(
          (item) => item.route !== window.location.pathname,
        )}
      />
      <Footer />
    </>
  );
};

export default Hydration;
